"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";

const PER_PAGE = 9;

export default function ArticlesClient({ initialArticles, initialPage, totalPages }) {
  const router = useRouter();

  const [articles, setArticles] = useState(initialArticles);
  const [page, setPage] = useState(initialPage || 1);
  const [total, setTotal] = useState(totalPages * PER_PAGE);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  // 🔍 debounced search
  useEffect(() => {
    const controller = new AbortController();

    const handler = setTimeout(async () => {
      try {
        setLoading(true);
        const query = searchTerm ? `?q=${encodeURIComponent(searchTerm)}&page=1` : "?page=1";
        const res = await fetch(`/api/articles/search${query}`, { signal: controller.signal });
        const data = await res.json();

        setArticles(data.results);
        setTotal(data.total); // теперь всегда точный total
        setPage(1);
      } catch (e) {
        if (e.name !== "AbortError") console.error(e);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(handler);
    };
  }, [searchTerm, initialArticles]);

  const loadPage = async (pageNum) => {
    try {
      setLoading(true);
      const res = await fetch(`/api/articles/search?q=${encodeURIComponent(searchTerm)}&page=${pageNum}`);
      const data = await res.json();

      setArticles(data.results);
      setPage(pageNum);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const totalPagesComputed = Math.ceil(total / PER_PAGE);

  return (
    <section className="max-w-[1200px] mx-auto px-6 pt-10 pb-10 text-center">
      {/* Search */}
      <div className="max-w-xs mx-auto mb-6">
        <input
          type="text"
          value={searchTerm}
          placeholder="Search by title..."
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm outline-none
    focus:outline-none
    focus:ring-0
    focus:border-gray-400"
        />
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {articles.map((t, i) => (
          <Link key={t.slug} href={`/article/${t.slug}`} className="flex flex-col justify-start bg-card-bg rounded-xl shadow-card hover:shadow-dark transition-all duration-200 p-6 text-card-text">
            <div className="relative w-full">
              <Image
                src={t.image}
                alt={t.title}
                width={i === 0 ? 480 : 400}
                height={i === 0 ? 210 : 175}
                sizes="(max-width: 768px) 100vw, 400px"
                quality={i === 0 ? 85 : 75}
                priority={i === 0}
                className="w-full h-auto rounded-lg object-cover block"
              />
            </div>
            <p className="text-title-green text-center font-semibold text-base mt-3 mb-2 leading-tight">{t.title}</p>

            <p className="text-card-text text-justify text-sm leading-relaxed">{t.previewText}</p>
          </Link>
        ))}
      </div>

      {/* Pagination */}
      <div className="flex justify-center items-center gap-4 mt-10 flex-wrap">
        {totalPagesComputed > 1 && (
          <>
            {page > 1 && (
              <button
                disabled={loading}
                onClick={() => loadPage(page - 1)}
                className="bg-white text-gray-800 border border-gray-300 rounded-lg px-6 py-2 text-sm font-medium transition duration-200 hover:bg-gray-50 cursor-pointer"
              >
                ← Previous
              </button>
            )}
            <span className="text-gray-800 text-sm font-medium">
              Page {page} of {totalPagesComputed}
            </span>
            {page < totalPagesComputed && (
              <button
                disabled={loading}
                onClick={() => loadPage(page + 1)}
                className="bg-white text-gray-800 border border-gray-300 rounded-lg px-6 py-2 text-sm font-medium transition duration-200 hover:bg-gray-50 cursor-pointer"
              >
                Next →
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
